import { ensureMessengerV2NotificationServiceWorker } from '../../../lib/messengerDesktopNotifications';
import { requestMessengerV2Json } from './messengerV2Api.js';

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const decodeBase64Url = (value) => {
  const normalized = toStringOrNull(value);
  if (!normalized) return null;
  const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
  const base64 = `${normalized}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const decoded = window.atob(base64);
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
};

export const canUseMessengerV2WebPush = () =>
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  'Notification' in window &&
  'serviceWorker' in navigator &&
  'PushManager' in window;

export const syncMessengerV2WebPushSubscription = async ({ clientId = null } = {}) => {
  if (!canUseMessengerV2WebPush() || window.Notification.permission !== 'granted') {
    return { configured: false, subscribed: false };
  }

  const registration = await ensureMessengerV2NotificationServiceWorker();
  if (!registration?.pushManager) {
    return { configured: false, subscribed: false };
  }

  const config = await requestMessengerV2Json(
    '/api/web_push_subscription',
    { method: 'GET' },
    'Unable to load push notification configuration',
  );
  const publicKey = toStringOrNull(config?.vapid_public_key);
  if (config?.configured !== true || !publicKey) {
    return { configured: false, subscribed: false };
  }

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: decodeBase64Url(publicKey),
    });
  }

  await requestMessengerV2Json(
    '/api/web_push_subscription',
    {
      method: 'POST',
      body: JSON.stringify({
        subscription: subscription.toJSON(),
        ...(toStringOrNull(clientId) ? { client_id: toStringOrNull(clientId) } : {}),
      }),
    },
    'Unable to register push notifications',
  );

  return { configured: true, subscribed: true, subscription };
};

export const removeMessengerV2WebPushSubscription = async () => {
  if (!canUseMessengerV2WebPush()) {
    return { removed: false };
  }

  const registration = await navigator.serviceWorker.getRegistration?.('/');
  const subscription = await registration?.pushManager?.getSubscription?.();
  if (!subscription) {
    return { removed: false };
  }

  const endpoint = toStringOrNull(subscription.endpoint);
  if (endpoint) {
    await requestMessengerV2Json(
      '/api/web_push_subscription',
      {
        method: 'DELETE',
        body: JSON.stringify({ endpoint }),
      },
      'Unable to remove push notifications',
    ).catch(() => null);
  }
  await subscription.unsubscribe();

  return { removed: true };
};

export default syncMessengerV2WebPushSubscription;
